import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'query-string';

import { useCheckEmailAvailabilityMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { emailAvailabilityStatus, queryStringifyOptions } from 'utils/constants';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { emailSchema } from 'containers/communication/EditableEmailAddress';
import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';

const CreateSubscriptionDialog = ({ onClose, open, fundId, onExternalFlow }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [externalDistributorEmail, setExternalDistributorEmail] = useState('');
    const [errors, setErrors] = useState({ email: '', externalDistributorEmail: '' });
    const [checkEmail, { isLoading: loading, isSuccess: checkSuccess, data: response, reset }] =
        useCheckEmailAvailabilityMutation();
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const handleChange = useCallback(e => {
        setEmail(e.target.value?.trim());
    }, []);

    const handleSubmit = useCallback(() => {
        setErrors({ email: '', externalDistributorEmail: '' });
        const { valid, newErrors } = validateSchema(
            {
                email: emailSchema,
                externalDistributorEmail: emailSchema.allow(''),
            },
            { email, externalDistributorEmail }
        );
        if (valid) {
            checkEmail(email);
        } else {
            setErrors(newErrors);
        }
    }, [checkEmail, email, externalDistributorEmail]);

    const handleKeyDown = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    useEffect(() => {
        if (checkSuccess) {
            if (response.status === emailAvailabilityStatus.AVAILABLE) {
                const queryObj = { email };
                if (
                    currentSubscription?.distributorCompany?.enableExternalDistributors &&
                    externalDistributorEmail
                ) {
                    queryObj.external_distributor_email = externalDistributorEmail;
                }
                if (fundId) {
                    // a product card was used to get here
                    // there's no user with the submitted email
                    queryObj.product = fundId;
                    onExternalFlow({ email });
                } else {
                    // the process was initiated on the subscriptions page
                    // the fund will be selected in the tunnel entry UI
                    navigate(
                        `/create-subscription?${qs.stringify(queryObj, queryStringifyOptions)}`
                    );
                }
            } else if (response.status === emailAvailabilityStatus.WITH_FINISHED_SUBSCRIPTION) {
                const queryObj = { userId: response.userId };
                if (fundId) {
                    // we know user ID and fund ID so we can already create the new indirect subscription
                    queryObj.product = fundId;
                    onExternalFlow({ userId: response.userId });
                } else {
                    navigate(
                        `/add-indirect-subscription?${qs.stringify(
                            queryObj,
                            queryStringifyOptions
                        )}`
                    );
                }
            } else {
                setErrors({ email: texts.EMAIL_UNAVAILABLE, externalDistributorEmail: '' });
            }
        }
    }, [checkSuccess, navigate, response, email, fundId, onExternalFlow, externalDistributorEmail]);

    useEffect(() => {
        if (!open) {
            setEmail('');
            setExternalDistributorEmail('');
            setErrors({ email: '', externalDistributorEmail: '' });
            reset();
        }
    }, [open, reset]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.CREATE_SUBSCRIPTION} handleClose={onClose} />
            <DialogContent onKeyDown={handleKeyDown}>
                <CustomTextField
                    value={email}
                    label={texts.EMAIL_SUBSCRIBER}
                    onChange={handleChange}
                    error={errors.email}
                />
                {currentSubscription?.distributorCompany?.enableExternalDistributors && (
                    <CustomTextField
                        value={externalDistributorEmail}
                        label={texts.EXTERNAL_DISTRIBUTOR_EMAIL}
                        onChange={e => setExternalDistributorEmail(e.target.value?.trim())}
                        error={errors.externalDistributorEmail}
                    />
                )}
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {texts.EMAIL_INFO_1}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {texts.EMAIL_INFO_2}
                </Typography>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={onClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton onClick={handleSubmit} loading={loading}>
                    {texts.CONFIRMER_CREATE_SUBSCRIPTION}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

CreateSubscriptionDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onExternalFlow: PropTypes.func,
    fundId: PropTypes.number,
};

export default CreateSubscriptionDialog;
